import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { History, RefreshCw, Ticket, Loader2, ScanLine } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatWhen = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const ScanHistory = () => {
  const token = localStorage.getItem('token');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/scan-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(res.data);
    } catch (e) {
      toast.error('Failed to load scan history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="space-y-6" data-testid="scan-history">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold tracking-tight text-white sm:text-3xl">Scan log</h2>
          <p className="mt-1 text-sm text-[#a1a1aa]">Every ticket issued by scanning a member's QR code.</p>
        </div>
        <Button
          data-testid="scan-history-refresh-btn"
          onClick={fetchHistory}
          variant="outline"
          className="h-10 w-10 rounded-full border-white/[0.08] p-0 text-[#a1a1aa] hover:text-white"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 text-[#EAB308] animate-spin" />
        </div>
      ) : history.length === 0 ? (
        <div className="text-center py-12 bg-[#18181b] border border-[#27272a] rounded-2xl" data-testid="scan-history-empty">
          <ScanLine className="w-10 h-10 text-[#52525b] mx-auto mb-3" />
          <p className="text-[#a1a1aa]">No scans yet. Use "Scan QR" to issue a ticket to a member.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="scan-history-list">
          {history.map((h) => (
            <div
              key={h.id}
              className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-[#18181b] px-4 py-3"
              data-testid={`scan-entry-${h.id}`}
            >
              <div className="bg-[#EAB308]/15 p-2 rounded-lg shrink-0">
                <Ticket className="w-4 h-4 text-[#EAB308]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">
                  {h.user_name}
                  <span className="text-[#52525b] font-normal"> · {h.whatsapp_number}</span>
                </p>
                <p className="text-xs text-[#a1a1aa] truncate">
                  Ticket #{h.ticket_number} · {h.draw_title}
                </p>
              </div>
              <span className="text-xs text-[#52525b] shrink-0 whitespace-nowrap">{formatWhen(h.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScanHistory;
